import React, { useState } from "react";
import { ExpandMore, ExpandLess, TrendingUp, TrendingDown } from "@mui/icons-material";

type SkillGapAssessmentAgentProps = {
  parsedContent: any;
};

type SkillGap = {
  skill_name: string;
  current_level: number;
  required_level: number;
  gap: number;
  priority: "High" | "Medium" | "Low";
  recommendation?: string;
};

type SkillCategory = {
  category: string;
  gaps: SkillGap[];
};

export default function SkillGapAssessmentAgent({
  parsedContent,
}: SkillGapAssessmentAgentProps): JSX.Element {
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});

  // Parse content - handle file upload responses
  const assessmentData = parsedContent?.finalAnswer || parsedContent?.answer || parsedContent;

  if (!assessmentData) {
    return (
      <div style={{ padding: "12px", color: "#666" }}>
        Processing skill gap assessment...
      </div>
    );
  }

  const {
    employee_name,
    role,
    assessment_date,
    skill_categories = [],
    overall_summary,
    recommendations = [],
  } = assessmentData;

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "High":
        return { bg: "#fef2f2", border: "#fca5a5", text: "#991b1b" };
      case "Medium":
        return { bg: "#fffbeb", border: "#fde68a", text: "#92400e" };
      case "Low":
        return { bg: "#f0fdf4", border: "#bbf7d0", text: "#166534" };
      default:
        return { bg: "#f9fafb", border: "#e5e7eb", text: "#6b7280" };
    }
  };

  const getGapIcon = (gap: number) => {
    if (gap > 0) {
      return <TrendingDown style={{ color: "#ef4444", fontSize: "18px" }} />;
    }
    return <TrendingUp style={{ color: "#10b981", fontSize: "18px" }} />;
  };

  return (
    <div style={{ width: "100%", color: "#333" }}>
      {/* Header Card */}
      <div
        style={{
          padding: "20px",
          backgroundColor: "#f0f9ff",
          borderRadius: "8px",
          border: "1px solid #bae6fd",
          marginBottom: "24px",
        }}
      >
        <h2 style={{ margin: "0 0 12px 0", color: "#0369a1", fontSize: "20px" }}>
          Skill Gap Assessment Report
        </h2>
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {employee_name && (
            <div style={{ fontSize: "14px", color: "#0c4a6e" }}>
              <strong>Employee:</strong> {employee_name}
            </div>
          )}
          {role && (
            <div style={{ fontSize: "14px", color: "#0c4a6e" }}>
              <strong>Role:</strong> {role}
            </div>
          )}
          {assessment_date && (
            <div style={{ fontSize: "14px", color: "#0c4a6e" }}>
              <strong>Assessment Date:</strong> {assessment_date}
            </div>
          )}
        </div>
      </div>

      {/* Skill Categories */}
      {skill_categories.length > 0 && (
        <div style={{ marginBottom: "24px" }}>
          <h3 style={{ fontSize: "16px", fontWeight: 600, color: "#374151", marginBottom: "16px" }}>
            Skill Gap Analysis by Category
          </h3>

          {skill_categories.map((category: SkillCategory, idx: number) => {
            const isExpanded = expandedCategories[category.category] !== false;
            const totalGaps = category.gaps.length;
            const highPriorityGaps = category.gaps.filter((g) => g.priority === "High").length;

            return (
              <div
                key={idx}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  marginBottom: "12px",
                  overflow: "hidden",
                }}
              >
                {/* Category Header */}
                <div
                  style={{
                    padding: "14px 16px",
                    backgroundColor: "#f9fafb",
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                  onClick={() => toggleCategory(category.category)}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    {isExpanded ? (
                      <ExpandLess style={{ fontSize: "20px", color: "#6b7280" }} />
                    ) : (
                      <ExpandMore style={{ fontSize: "20px", color: "#6b7280" }} />
                    )}
                    <span style={{ fontWeight: 600, fontSize: "14px", color: "#111827" }}>
                      {category.category}
                    </span>
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#666",
                        backgroundColor: "#f5f5f5",
                        padding: "2px 8px",
                        borderRadius: "4px",
                      }}
                    >
                      {totalGaps} skills
                    </span>
                    {highPriorityGaps > 0 && (
                      <span
                        style={{
                          fontSize: "11px",
                          fontWeight: 600,
                          color: "#991b1b",
                          backgroundColor: "#fef2f2",
                          padding: "2px 8px",
                          borderRadius: "4px",
                          border: "1px solid #fca5a5",
                        }}
                      >
                        {highPriorityGaps} high priority
                      </span>
                    )}
                  </div>
                </div>

                {/* Skills List */}
                {isExpanded && (
                  <div style={{ padding: "16px", backgroundColor: "#ffffff" }}>
                    {category.gaps.map((gap: SkillGap, gapIdx: number) => {
                      const priorityColors = getPriorityColor(gap.priority);

                      return (
                        <div
                          key={gapIdx}
                          style={{
                            padding: "12px",
                            backgroundColor: priorityColors.bg,
                            border: `1px solid ${priorityColors.border}`,
                            borderRadius: "6px",
                            marginBottom: gapIdx < category.gaps.length - 1 ? "12px" : "0",
                          }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600, fontSize: "14px", color: priorityColors.text, marginBottom: "4px" }}>
                                {gap.skill_name}
                              </div>
                              <div style={{ display: "flex", gap: "16px", fontSize: "12px", color: "#4b5563" }}>
                                <span>Current: <strong>{gap.current_level}/5</strong></span>
                                <span>Required: <strong>{gap.required_level}/5</strong></span>
                                <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                  Gap: <strong style={{ color: priorityColors.text }}>{Math.abs(gap.gap)}</strong>
                                  {getGapIcon(gap.gap)}
                                </span>
                              </div>
                            </div>
                            <div
                              style={{
                                fontSize: "11px",
                                fontWeight: 600,
                                color: priorityColors.text,
                                backgroundColor: "#fff",
                                padding: "4px 10px",
                                borderRadius: "4px",
                                border: `1px solid ${priorityColors.border}`,
                              }}
                            >
                              {gap.priority} Priority
                            </div>
                          </div>
                          {gap.recommendation && (
                            <div style={{ fontSize: "12px", color: "#4b5563", lineHeight: "1.6", marginTop: "8px", paddingTop: "8px", borderTop: "1px solid #e5e7eb" }}>
                              <strong style={{ color: priorityColors.text }}>Recommendation:</strong> {gap.recommendation}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Overall Summary */}
      {overall_summary && (
        <div
          style={{
            padding: "16px",
            backgroundColor: "#f8fafc",
            borderRadius: "8px",
            border: "1px solid #e2e8f0",
            marginBottom: "16px",
          }}
        >
          <div style={{ fontSize: "14px", fontWeight: 600, color: "#334155", marginBottom: "8px" }}>
            Overall Summary
          </div>
          <div style={{ fontSize: "13px", color: "#475569", lineHeight: "1.6" }}>
            {overall_summary}
          </div>
        </div>
      )}

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <div
          style={{
            padding: "16px",
            backgroundColor: "#f0fdf4",
            borderRadius: "8px",
            border: "1px solid #bbf7d0",
          }}
        >
          <div style={{ fontSize: "14px", fontWeight: 600, color: "#166534", marginBottom: "12px" }}>
            📋 Key Recommendations
          </div>
          <ul style={{ margin: 0, paddingLeft: "20px", fontSize: "13px", color: "#14532d", lineHeight: "1.8" }}>
            {recommendations.map((rec: string, idx: number) => (
              <li key={idx} style={{ marginBottom: "8px" }}>
                {rec}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
